/**
 * Whether the stored session token has expired, decided from the token itself.
 *
 * <h3>Why this cannot be decided from the HTTP status</h3>
 * The backends disagree. Verified 2026-09-08 against a missing, a malformed and
 * an expired token:
 *
 * <pre>
 *   api-staging.greengiraffe.in  ->  401, 401, 401
 *   api-tvoice.teeoff.tech       ->  403, 403, 403
 * </pre>
 *
 * The response interceptor logs out on 401 and deliberately NOT on 403, because
 * a 403 usually means "authenticated but not permitted" and logging those out
 * would throw people back to the login page for clicking something they lack
 * rights to. On the tvoice backend that reasoning is inverted: an expired token
 * IS a 403, so the app kept a dead token, every request failed, and nobody was
 * ever logged out.
 *
 * Reading `exp` off the token settles it without guessing at either backend's
 * conventions — and a real permission 403 held with a VALID token still does not
 * log anyone out.
 */

/** Seconds since the epoch in the token's `exp`, or null if unreadable. */
export function tokenExpiry(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url -> base64 before atob, which does not accept - or _.
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const claims = JSON.parse(atob(b64));
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    // Unreadable is NOT expired. A token we cannot parse might still be one the
    // server accepts, and logging out on it would be a logout loop for anyone
    // whose token shape we do not recognise.
    return null;
  }
}

/**
 * True only when the token is definitely past its expiry.
 *
 * <p>Absent or unparseable returns FALSE — "expired" has to be something we
 * established, not something we failed to rule out. The interceptor pairs this
 * with the response status, so an unreadable token still logs out on a 401.
 */
export function isTokenExpired(token: string | null | undefined, nowMs: number = Date.now()): boolean {
  const exp = tokenExpiry(token);
  if (exp === null) return false;
  return exp * 1000 <= nowMs;
}
